import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/api";

// Defaults come straight from the design system.
const DEFAULTS = {
  primaryHsl: "239 84% 67%",
  accentHsl: "280 65% 60%",
  fontFamily: "'Inter', system-ui, sans-serif",
};

const STORAGE_KEY = "fs_styling";
const StylingContext = createContext(null);

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

// Batch V1.4 #10 — normalize a branding shape (from the /api/settings/branding
// server response) to the overrides shape stored in localStorage.
function brandingToOverrides(br) {
  if (!br) return {};
  const out = {};
  if (br.primary_hsl) out.primaryHsl = br.primary_hsl;
  if (br.accent_hsl) out.accentHsl = br.accent_hsl;
  if (br.font_family) out.fontFamily = `'${br.font_family}', system-ui, sans-serif`;
  return out;
}

export function StylingProvider({ children }) {
  const [overrides, setOverrides] = useState(readStorage);

  const config = useMemo(() => ({ ...DEFAULTS, ...overrides }), [overrides]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primary", config.primaryHsl);
    root.style.setProperty("--accent", config.accentHsl);
    root.style.setProperty("--ring", config.primaryHsl);
    document.body.style.fontFamily = config.fontFamily;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  }, [config, overrides]);

  // V1.4 #10 — hydrate from the server on mount so a fresh browser
  // gets the operator's chosen branding without waiting for login.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/settings/branding`).then(({ data }) => {
      if (cancelled) return;
      const patch = brandingToOverrides(data);
      if (Object.keys(patch).length) setOverrides((prev) => ({ ...prev, ...patch }));
    }).catch(() => { /* silent — defaults are fine */ });
    return () => { cancelled = true; };
  }, []);

  const update = useCallback((patch) => {
    setOverrides((prev) => ({ ...prev, ...patch }));
  }, []);

  // V1.4 #10 — accept a branding shape from the Branding page.
  const applyBranding = useCallback((br) => {
    const patch = brandingToOverrides(br);
    if (Object.keys(patch).length) setOverrides((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setOverrides({}), []);

  return (
    <StylingContext.Provider value={{ config, update, applyBranding, reset }}>
      {children}
    </StylingContext.Provider>
  );
}

export function useStyling() {
  const ctx = useContext(StylingContext);
  if (!ctx) throw new Error("useStyling must be used inside <StylingProvider>");
  return ctx;
}

